import CancelIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import isEqual from "lodash.isequal";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployee, updateEmployee } from "../api/employee";
import { isUserAuthorizedByServer } from "../api/userAccess";
import PageLoading from "../components/PageLoading";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import SeniorityDates from "../components/edm/SeniorityDates";
import PersonalInfo from "../components/edm/PersonalInfo";
import ViewCommentsDialog from "../components/ViewCommentsDialog";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getFormattedPhoneNumber, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CONTRACT_TO_SENIORITY_DATES_MAP,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  EMPLOYEE_ID,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  STATION,
  EDM_CHAR_LIMIT,
  SUBMIT,
  CANCEL,
} from "../util/constants";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import { findModifiedItems } from "../util/edm/edmUtils";
import { validateDate, validateFtPt, validatePhoneNumber } from "../util/edm/inputValidation";
import { validateEmployee, validateEmployeeId } from "../util/inputValidation";

import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_SAVE_CHANGES_DIALOG_MESSAGE,
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  EDM_INFO_MESSAGE,
  EMPLOYEE_DATA_CONFIRMATION_TITLE,
  EMPLOYEE_DATA_MANAGEMENT_SUBMISSION_ERROR_MESSAGE,
  INVALID_PHONE_NUMBER,
  EMPLOYEE_DATA_SAVE_MESSAGE,
  SAVE_CHANGES_TITLE,
  STATION_FILTER_MUST_BE_SELECTED,
} from "../util/messageConstants";
import { validateEmployeeDataForAdminCreateTimeOffRequest } from "../util/timeOffRequestInputValidationAdmin";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";
import { selectJobCodeData } from "../slices/commonConfig/commonConfigSlice";

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: "4.3vw",
    height: "calc(100vh - 64px)",
    overflowY: "scroll",
  },
  buttonStyle: {
    padding: "15px",
  },
  employeeManagementDetailsAndTitle: {
    display: "flex",
    marginLeft: 6,
  },
  cardStyle: {
    width: "500px",
    marginTop: "30px",
    marginLeft: "28px",
  },
  cardView: {
    margin: "5px",
    textAlign: "left",
  },
  inputSelect: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
    width: "90px",
  },
  inputText: {
    width: "247px",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  bidInputRow: {
    display: "flex",
    paddingTop: "16px",
    paddingLeft: "32px",
  },
  bidInput: {
    paddingLeft: "10px",
  },
  eid: {
    color: theme.palette.secondary.swaGray,
  },
  infoMessage: {
    marginBottom: "10px",
  },
}));
const EmployeeDataManagement = (props) => {
  // Redux State
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const jobCodeData = useSelector(selectJobCodeData);
  const [station, setStation] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeContract, setEmployeeContract] = useState("");
  const [employeeDataInitialState, setEmployeeDataInitialState] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeDataErrors, setEmployeeDataErrors] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const [employeeWorkgroup, setEmployeeWorkgroup] = useState("");
  const [isAreYouDialogOpen, toggleAreYouSureDialog] = useState(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isErrorEmployeeDataSave, setIsErrorEmployeeDataSave] = useState(false);
  const [saveEmployeeDataLoading, setSaveEmployeeDataLoading] = useState(false);
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.EMPLOYEE_DATA_MANAGEMENT];
  const breadcrumbsLink = [HOME_PATH];
  const dispatch = useDispatch();
  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };
  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      setStation(getAdminStation(props.featureConfig.userStations, userSession, stationFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const clearBottomForm = () => {
    setEmployeeIdErrors("");
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(false);
    setEmployeeId("");
    setEmployeeData(null);
    setEmployeeDataErrors(null);
  };
  const handleStationChange = (selectedStation) => {
    clearBottomForm();
    setStation(selectedStation.target.value);
    dispatch(updateStationFilter(selectedStation.target.value));
  };
  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };
  const handleResetClick = () => {
    fetchEmployeeData(employeeData.eid);
  };
  const handleEmployeeIdChange = (event) => {
    if (employeeData) {
      setEmployeeData(null);
      setEmployeeDataErrors(null);
      setEmployeeDataInitialState(null);
    }
    setEmployeeIdErrors("");
    setEmployeeId(event?.target?.value?.trim()?.toLowerCase());
  };
  const handlePhoneNumberChange = (newPhoneNumber) => {
    const error = validatePhoneNumber(newPhoneNumber, INVALID_PHONE_NUMBER);

    setEmployeeDataErrors((employeeDataErrors) => ({
      ...employeeDataErrors,
      phoneNumber: error,
    }));

    setEmployeeData((employeeData) => ({
      ...employeeData,
      phoneNumber: error ? newPhoneNumber : getFormattedPhoneNumber(newPhoneNumber),
    }));
  };
  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setEmployeeData(null);
    setEmployeeDataErrors(null);
    setEmployeeDataInitialState(null);
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };
  const handleNoClickedCancelDialog = () => {
    toggleAreYouSureDialog(false);
    setIsSaveButtonClicked(false);
    setSaveEmployeeDataLoading(false);
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments("");
  };
  const handleOnYesButtonClick = async () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > EDM_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(EDM_CHAR_LIMIT));
    } else {
      let payload = {
        eid: employeeData.eid,
        createdBy: "CLIENT",
        updateType: "ADD_FIELDS",
        items: {
          ...findModifiedItems(employeeDataInitialState, employeeData),
          comments,
          modifiedByEid: userSession.eid,
          modifiedByFirstName: userSession.firstName,
          modifiedByLastName: userSession.lastName,
        },
        v: employeeData.v,
      };
      setSaveEmployeeDataLoading(true);
      await updateEmployee(payload)
        .then((res) => res.json())
        .then(async (result) => {
          if (result.pk) {
            setIsSaveButtonClicked(false);
            setIsSuccessDialogOpen(true);
            setSaveEmployeeDataLoading(false);
          }
        })
        .catch((err) => {
          console.log(err, "error");
          setIsSuccessDialogOpen(true);
          setIsErrorEmployeeDataSave(true);
          setIsSaveButtonClicked(false);
          setSaveEmployeeDataLoading(false);
        });
    }
  };

  const handleSuccessDialogOKClick = () => {
    setIsSuccessDialogOpen(false);
    fetchEmployeeData(employeeData.eid);
    setCommentErrorMessage("");
    setComments("");
  };

  const handleErrorDialogOKClick = () => {
    setIsSuccessDialogOpen(false);
    setCommentErrorMessage("");
    setComments("");
  };
  const handleEmployeeIdBlur = (event) => {
    let eid = event?.target?.value;
    let errors = validateEmployeeId(eid);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || eid !== employeeData.eid) {
        fetchEmployeeData(eid);
      }
    }
  };
  const handleView = (eid) => {
    if (!employeeData || eid !== employeeData.eid) {
      let errors = validateEmployeeId(eid);
      if (errors === "") fetchEmployeeData(eid);
      if (employeeIdErrors === "" || errors !== "") {
        setEmployeeIdErrors(errors);
      }
    }
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <>
        {isSaveButtonClicked && (
          <ConfirmationDialog
            isDialogOpen={isAreYouDialogOpen}
            title={SAVE_CHANGES_TITLE}
            message={ARE_YOU_SURE_YOU_WANT_TO_SAVE_CHANGES_DIALOG_MESSAGE(
              employeeData.lastName,
              employeeData.firstName
            )}
            handleNoButtonClicked={handleNoClickedCancelDialog}
            handleYesButtonClicked={handleOnYesButtonClick}
            isYesLoading={saveEmployeeDataLoading}
            hasComments
            comments={comments}
            commentsError={commentsError}
            commentErrorMessage={commentErrorMessage}
            setComments={setComments}
            setCommentsError={setCommentsError}
            setCommentErrorMessage={setCommentErrorMessage}
            commentCharLimit={EDM_CHAR_LIMIT}
            yesTitle={SUBMIT}
            noTitle={CANCEL}
          />
        )}
      </>
    );
  };

  const fetchEmployeeData = async (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    setEmployeeDataErrors(null);
    setEmployeeDataInitialState(null);
    await fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let filteredEmployeeData = await getUserDataFromEmployeeService(result);
          setEmployeeContract(jobCodeData[filteredEmployeeData.jobCode].unionContract);
          setEmployeeWorkgroup(jobCodeData[filteredEmployeeData.jobCode].workgroup);
          let employeeDataErrors = validateEmployeeDataForAdminCreateTimeOffRequest(filteredEmployeeData, station);
          if (employeeDataErrors === "") {
            employeeDataErrors = validateEmployee(
              filteredEmployeeData.eid,
              filteredEmployeeData.firstName,
              filteredEmployeeData.lastName,
              filteredEmployeeData.activeStation,
              jobCodeData[filteredEmployeeData.jobCode].workgroup
            );
          }
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          }
          setEmployeeData(filteredEmployeeData);
          setEmployeeDataInitialState(filteredEmployeeData);
          setEmployeeIdSearchLoaded(true);
        } else {
          setEmployeeIdErrors("Invalid Employee ID");
          setEmployeeIdSearchLoaded(true);
        }
      });
  };
  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const handleEmployeeFieldChange = (name, value) => {
    //set field errors to empty since field is changed and value always present because those are dropdowns and datepickers
    setEmployeeDataErrors((employeeDataErrors) => ({
      ...employeeDataErrors,
      [name]: "",
    }));

    setEmployeeData((employeeData) => ({
      ...employeeData,
      [name]: value,
    }));
  };

  const isEmployeeDataChanged = () => {
    return !isEqual(employeeData, employeeDataInitialState);
  };

  const validateDateFields = () => {
    const dateErrors = {};
    CONTRACT_TO_SENIORITY_DATES_MAP[employeeContract].forEach((date) => {
      const error = date.isRequired && validateDate(date.label, employeeData[date.key]);
      if (error) {
        dateErrors[date.key] = error;
      }
    });

    setEmployeeDataErrors((employeeDataErrors) => ({
      ...employeeDataErrors,
      ...dateErrors,
    }));

    return Object.keys(dateErrors).length === 0;
  };

  const validatePhoneNumberField = () => {
    return !employeeDataErrors?.phoneNumber;
  };

  const validateFtPtField = () => {
    const ftPtError = validateFtPt(employeeData.ftpt);

    if (ftPtError) {
      setEmployeeDataErrors((employeeDataErrors) => ({
        ...employeeDataErrors,
        ftpt: ftPtError,
      }));

      return false;
    }

    return true;
  };

  const areAllValidationsPassing = () => {
    const isPhoneNumberValidationPassing = validatePhoneNumberField();
    const isFtPtValidationPassing = validateFtPtField();
    const isDatesValidationPassing = validateDateFields();

    return isPhoneNumberValidationPassing && isFtPtValidationPassing && isDatesValidationPassing;
  };

  const handleSaveClick = () => {
    if (areAllValidationsPassing()) {
      toggleAreYouSureDialog(true);
      setIsSaveButtonClicked(true);
    }
  };

  const isModifiedByClient = () => {
    return (
      employeeData &&
      employeeData.createdBy === "CLIENT" &&
      employeeData.modifiedByEid &&
      employeeData.modifiedByLastName &&
      employeeData.modifiedByFirstName &&
      employeeData.createdAt
    );
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(employeeData.createdAt, station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const renderDesktopEmployeeDataManagementPage = () => {
    return (
      <div className={classes.root}>
        <div>
          <StarsBreadcrumbs
            isConfirmationRequired={isEmployeeDataChanged()}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={breadcrumbsList}
            cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
          />
        </div>
        <Grid justifyContent="flex-start" className={classes.employeeManagementDetailsAndTitle}>
          <div className={`page-title-left`}>Employee Data Management</div>
        </Grid>
        <Grid sx={{ marginLeft: "31px" }}>
          <Alert severity="info" sx={{ marginRight: "10px", textAlign: "left", maxWidth: "75vw" }}>
            {EDM_INFO_MESSAGE}
          </Alert>
        </Grid>
        <div className={classes.bidInputRow}>
          <div>
            {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
              <FormControl fullWidth>
                <StarsSelectInput
                  label={STATION}
                  menuMessage={STATION}
                  value={stationFilter}
                  allOptions={getMultiStationAdminStationList(userSession)}
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => handleStationChange(event)}
                  errors={stationFilter === "" ? STATION_FILTER_MUST_BE_SELECTED : ""}
                  className={classes.inputSelect}
                  isMenuItemDisabled={(station) => !props?.featureConfig?.userStations?.includes(station)}
                />
              </FormControl>
            ) : (
              <TextField
                className={classes.inputSelect}
                label={STATION}
                variant="outlined"
                value={stationFilter}
                disabled
              />
            )}
          </div>
          <div className={classes.bidInput}>
            <TextField
              data-testid="employeeDataManagement-eid-textField"
              className={classes.inputText}
              label={EMPLOYEE_ID}
              variant="outlined"
              value={employeeId}
              onChange={handleEmployeeIdChange}
              onBlur={handleEmployeeIdBlur}
              onKeyUp={handleEmployeeIdEnterKeyPressed}
              InputProps={{
                endAdornment:
                  employeeId !== "" ? (
                    <InputAdornment position="start">
                      <CancelButtonEmployeeId />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              error={employeeIdErrors !== ""}
              helperText={employeeIdErrors}
            />
          </div>
          <div>
            <Button
              data-testid="employeeDataManagement-view-button"
              className={classes.buttonStyle}
              variant="contained"
              color="primary"
              onClick={() => handleView(employeeId)}
            >
              View
            </Button>
          </div>
        </div>
        {employeeIdSearchTriggered &&
          (employeeIdSearchLoaded ? (
            employeeIdErrors === "" &&
            employeeData &&
            employeeContract && (
              <div className={classes.cardView}>
                <Card className={classes.cardStyle} variant="outlined">
                  <CardContent sx={{ paddingLeft: "30px", paddingRight: "20px" }}>
                    <Grid container marginBottom={1} marginTop={1} alignItems="start">
                      <Grid item xs={6}>
                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                          {employeeData.lastName + ", " + employeeData.firstName}
                        </Typography>
                        <Typography variant="body2" className={classes.eid}>
                          {employeeData.eid}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Grid container justifyContent="flex-end" spacing={2}>
                          <Grid item>
                            <Button className="button" size="small" onClick={handleResetClick} variant="outlined">
                              Cancel
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              className="button"
                              variant="contained"
                              color="primary"
                              size="small"
                              disabled={!isEmployeeDataChanged()}
                              onClick={handleSaveClick}
                            >
                              Save
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {isModifiedByClient() ? (
                      <div>
                        <Typography variant="subtitle2">
                          {`Last updated by ${employeeData.modifiedByLastName}, ${
                            employeeData.modifiedByFirstName
                          } on ${getLastUpdatedTimeString()} `}
                        </Typography>
                        {employeeData.comments && (
                          <Link onClick={() => setViewCommentsDialogOpen(true)} component="button" variant="subtitle2">
                            (View comment)
                          </Link>
                        )}
                      </div>
                    ) : (
                      employeeData.createdAt && <div>{`Last updated on ${getLastUpdatedTimeString()}`}</div>
                    )}
                    <PersonalInfo
                      employeeData={employeeData}
                      employeeDataErrors={employeeDataErrors}
                      handleEmployeeFieldChange={handleEmployeeFieldChange}
                      handlePhoneNumberChange={handlePhoneNumberChange}
                      workgroup={employeeWorkgroup}
                    />
                    <SeniorityDates
                      contract={employeeContract}
                      employeeData={employeeData}
                      employeeDataErrors={employeeDataErrors}
                      handleEmployeeFieldChange={handleEmployeeFieldChange}
                      station={station}
                    />
                  </CardContent>
                </Card>
              </div>
            )
          ) : (
            <CircularProgress className={classes.employeeInfoLoading} />
          ))}
        {renderCancelAreYouSureDialog()}
        {isSuccessDialogOpen && (
          <SuccessDialog
            isSuccessDialogOpen={isSuccessDialogOpen}
            title={EMPLOYEE_DATA_CONFIRMATION_TITLE}
            message={EMPLOYEE_DATA_SAVE_MESSAGE}
            redirectTo={props?.featureConfig?.adminPath}
            handleOKClick={isErrorEmployeeDataSave ? handleErrorDialogOKClick : handleSuccessDialogOKClick}
            hasError={isErrorEmployeeDataSave}
            error={EMPLOYEE_DATA_MANAGEMENT_SUBMISSION_ERROR_MESSAGE}
          />
        )}
        {viewCommentsDialogOpen && (
          <ViewCommentsDialog
            isDialogOpen={viewCommentsDialogOpen}
            handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
            header={`Last updated by ${employeeData.modifiedByLastName}, ${employeeData.modifiedByFirstName}`}
            publishedTimestamp={getLastUpdatedTimeString()}
            message={employeeData.comments}
          />
        )}
      </div>
    );
  };

  const renderMobileEmployeeDataManagementPage = () => {
    return (
      <div className="page-error" data-testid="employee-data-management-mobile-page">
        {ADMIN_MOBILE_WARNING_MESSAGE}
      </div>
    );
  };
  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="employee-data-management-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {props.isDesktopView ? renderDesktopEmployeeDataManagementPage() : renderMobileEmployeeDataManagementPage()}
        </>
      )}
    </div>
  );
};

export default EmployeeDataManagement;
